import React, { useState, useEffect, useCallback } from "react";
import {
  Key,
  Plus,
  Trash2,
  Copy,
  Check,
  AlertTriangle,
  RefreshCw,
  Eye,
  EyeOff,
} from "lucide-react";
import { getApiUrl } from "../services/logsApi";

const API_URL = getApiUrl();

const t = {
  primary: "#7c3aed",
  primaryLight: "#ede9fe",
  bg: "#f5f3ff",
  surface: "#ffffff",
  border: "#e9e5f5",
  danger: "#ef4444",
  dangerBg: "rgba(239,68,68,0.08)",
  success: "#22c55e",
  text: { primary: "#1e1b4b", secondary: "#6b7280", muted: "#9ca3b8" },
};

interface ApiKey {
  id: string;
  name: string;
  description: string;
  keyPreview?: string;
  key?: string; // only present at creation
  active: boolean;
  createdBy: string;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt?: string;
}

function timeAgo(iso: string | null): string {
  if (!iso) return "Jamais";
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return "À l'instant";
  const m = Math.floor(s / 60);
  if (m < 60) return `Il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `Il y a ${h}h`;
  const d = Math.floor(h / 24);
  return `Il y a ${d}j`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handle = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handle}
      title="Copier"
      className="flex items-center justify-center w-8 h-8 rounded-lg transition-colors"
      style={{
        background: copied ? t.primaryLight : "transparent",
        border: `1px solid ${t.border}`,
        cursor: "pointer",
      }}
    >
      {copied ? (
        <Check className="w-3.5 h-3.5" style={{ color: t.primary }} />
      ) : (
        <Copy className="w-3.5 h-3.5" style={{ color: t.text.muted }} />
      )}
    </button>
  );
}

// Modal shown after key creation — full key visible only once
function NewKeyModal({
  apiKey,
  onClose,
}: {
  apiKey: ApiKey;
  onClose: () => void;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
      style={{
        backgroundColor: "rgba(30,27,75,0.55)",
        backdropFilter: "blur(6px)",
      }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6"
        style={{ border: `1px solid ${t.border}` }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: "#dcfce7" }}
          >
            <Check className="w-5 h-5" style={{ color: "#16a34a" }} />
          </div>
          <div>
            <h2
              className="text-[16px] font-bold"
              style={{ color: t.text.primary }}
            >
              Clé créée — copiez-la maintenant
            </h2>
            <p className="text-[12px]" style={{ color: t.text.secondary }}>
              Elle ne sera plus affichée après fermeture.
            </p>
          </div>
        </div>

        <div
          className="rounded-xl p-3 mb-4 flex items-center gap-2"
          style={{ backgroundColor: t.bg, border: `1px solid ${t.border}` }}
        >
          <code
            className="flex-1 text-[13px] font-mono break-all"
            style={{ color: t.primary }}
          >
            {visible
              ? apiKey.key
              : `${apiKey.key!.slice(0, 12)}${"•".repeat(24)}`}
          </code>
          <button
            onClick={() => setVisible((v) => !v)}
            className="p-1.5 rounded-lg"
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: t.text.muted,
            }}
          >
            {visible ? (
              <EyeOff className="w-4 h-4" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
          </button>
          <CopyButton text={apiKey.key!} />
        </div>

        <div
          className="rounded-xl p-3 mb-5 text-[12px]"
          style={{
            backgroundColor: "#fffbeb",
            border: "1px solid #fbbf24",
            color: "#92400e",
          }}
        >
          <p className="font-semibold mb-1">Usage depuis un site externe :</p>
          <code className="break-all">{`POST https://activdev.nosylabs.fr/api/logs\nX-Api-Key: ${apiKey.key}\nContent-Type: application/json`}</code>
        </div>

        <button
          onClick={onClose}
          className="w-full py-2.5 rounded-xl text-[13px] font-semibold"
          style={{
            background: t.primary,
            color: "#fff",
            border: "none",
            cursor: "pointer",
          }}
        >
          J'ai copié ma clé
        </button>
      </div>
    </div>
  );
}

// Confirm revoke/delete modal
function ConfirmModal({
  message,
  onConfirm,
  onCancel,
}: {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
      style={{
        backgroundColor: "rgba(30,27,75,0.55)",
        backdropFilter: "blur(6px)",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6"
        style={{ border: `1px solid ${t.border}` }}
      >
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 mx-auto"
          style={{ backgroundColor: t.dangerBg }}
        >
          <AlertTriangle className="w-5 h-5" style={{ color: t.danger }} />
        </div>
        <p
          className="text-[14px] font-medium mb-5 text-center"
          style={{ color: t.text.primary }}
        >
          {message}
        </p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 rounded-xl text-[13px] font-medium"
            style={{
              border: `1px solid ${t.border}`,
              background: "#f9f8ff",
              color: t.text.primary,
              cursor: "pointer",
            }}
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-2.5 rounded-xl text-[13px] font-semibold"
            style={{
              background: t.danger,
              color: "#fff",
              border: "none",
              cursor: "pointer",
            }}
          >
            Confirmer
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ApiKeys() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [formError, setFormError] = useState("");
  const [newKeyResult, setNewKeyResult] = useState<ApiKey | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    message: string;
    action: () => void;
  } | null>(null);

  const token = localStorage.getItem("logs_dashboard_token");
  const authHeader = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/apikeys`, {
        headers: authHeader,
      });
      const data = await res.json();
      if (data.success) setKeys(data.data);
      else setError(data.error || "Erreur de chargement");
    } catch {
      setError("Impossible de contacter le serveur");
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");
    if (!newName.trim()) {
      setFormError("Le nom est requis");
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/apikeys`, {
        method: "POST",
        headers: { ...authHeader, "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newName.trim(),
          description: newDesc.trim(),
        }),
      });
      const data = await res.json();
      if (data.success) {
        setNewKeyResult(data.data);
        setNewName("");
        setNewDesc("");
        setCreating(false);
        load();
      } else {
        setFormError(data.error || "Erreur lors de la création");
      }
    } catch {
      setFormError("Erreur réseau");
    }
  };

  const revokeKey = async (id: string) => {
    await fetch(`${API_URL}/api/apikeys/${id}`, {
      method: "DELETE",
      headers: authHeader,
    });
    load();
  };

  const deleteKey = async (id: string) => {
    await fetch(`${API_URL}/api/apikeys/${id}/permanent`, {
      method: "DELETE",
      headers: authHeader,
    });
    load();
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: t.primaryLight }}
          >
            <Key className="w-5 h-5" style={{ color: t.primary }} />
          </div>
          <div>
            <h1
              className="text-[20px] font-bold"
              style={{ color: t.text.primary }}
            >
              Clés API
            </h1>
            <p className="text-[12px]" style={{ color: t.text.muted }}>
              Permettre à des services externes d'envoyer des logs
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="p-2 rounded-lg transition-colors"
            title="Actualiser"
            style={{
              border: `1px solid ${t.border}`,
              background: t.surface,
              color: t.text.muted,
              cursor: "pointer",
            }}
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => setCreating(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-[13px] font-semibold transition-all"
            style={{
              background: t.primary,
              color: "#fff",
              border: "none",
              cursor: "pointer",
              boxShadow: "0 4px 12px rgba(124,58,237,0.3)",
            }}
          >
            <Plus className="w-4 h-4" />
            Nouvelle clé
          </button>
        </div>
      </div>

      {/* Usage card */}
      <div
        className="rounded-2xl p-5 mb-6"
        style={{ backgroundColor: t.surface, border: `1px solid ${t.border}` }}
      >
        <p
          className="text-[13px] font-semibold mb-2"
          style={{ color: t.text.primary }}
        >
          Comment utiliser une clé API
        </p>
        <div
          className="rounded-xl p-3 text-[12px] font-mono overflow-x-auto"
          style={{
            backgroundColor: t.bg,
            border: `1px solid ${t.border}`,
            color: t.text.secondary,
          }}
        >
          <div>
            <span style={{ color: t.text.muted }}>POST</span>{" "}
            <span style={{ color: t.primary }}>
              https://activdev.nosylabs.fr/api/logs
            </span>
          </div>
          <div>
            <span style={{ color: "#f59e0b" }}>X-Api-Key</span>:{" "}
            <span style={{ color: t.text.secondary }}>lk_votre_cle_ici</span>
          </div>
          <div>
            <span style={{ color: "#f59e0b" }}>Content-Type</span>:{" "}
            <span style={{ color: t.text.secondary }}>application/json</span>
          </div>
          <div className="mt-2">{`{ "level": "error", "message": "...", "service": "mon-app" }`}</div>
        </div>
      </div>

      {/* Create form */}
      {creating && (
        <div
          className="rounded-2xl p-5 mb-6"
          style={{
            backgroundColor: t.surface,
            border: `1px solid ${t.primary}`,
            boxShadow: `0 0 0 3px ${t.primaryLight}`,
          }}
        >
          <h3
            className="text-[14px] font-bold mb-4"
            style={{ color: t.text.primary }}
          >
            Nouvelle clé API
          </h3>
          <form onSubmit={handleCreate} className="space-y-3">
            <div>
              <label
                className="block text-[12px] font-medium mb-1"
                style={{ color: t.text.primary }}
              >
                Nom <span style={{ color: t.danger }}>*</span>
              </label>
              <input
                type="text"
                value={newName}
                onChange={(e) => {
                  setNewName(e.target.value);
                  setFormError("");
                }}
                placeholder="ex: Site vitrine, App mobile…"
                className="w-full h-10 px-3 rounded-lg text-[13px] outline-none"
                style={{
                  border: `1px solid ${formError ? t.danger : t.border}`,
                  backgroundColor: t.bg,
                  color: t.text.primary,
                }}
              />
              {formError && (
                <p className="mt-1 text-[11px]" style={{ color: t.danger }}>
                  {formError}
                </p>
              )}
            </div>
            <div>
              <label
                className="block text-[12px] font-medium mb-1"
                style={{ color: t.text.primary }}
              >
                Description
              </label>
              <input
                type="text"
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                placeholder="Usage optionnel…"
                className="w-full h-10 px-3 rounded-lg text-[13px] outline-none"
                style={{
                  border: `1px solid ${t.border}`,
                  backgroundColor: t.bg,
                  color: t.text.primary,
                }}
              />
            </div>
            <div className="flex gap-2 pt-1">
              <button
                type="submit"
                className="px-4 py-2 rounded-xl text-[13px] font-semibold"
                style={{
                  background: t.primary,
                  color: "#fff",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Générer la clé
              </button>
              <button
                type="button"
                onClick={() => {
                  setCreating(false);
                  setFormError("");
                  setNewName("");
                  setNewDesc("");
                }}
                className="px-4 py-2 rounded-xl text-[13px] font-medium"
                style={{
                  border: `1px solid ${t.border}`,
                  background: "transparent",
                  color: t.text.secondary,
                  cursor: "pointer",
                }}
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Error */}
      {error && (
        <div
          className="rounded-xl p-3 mb-4 text-[13px]"
          style={{
            backgroundColor: t.dangerBg,
            border: `1px solid ${t.danger}`,
            color: t.danger,
          }}
        >
          {error}
        </div>
      )}

      {/* Keys list */}
      <div
        className="rounded-2xl overflow-hidden"
        style={{ backgroundColor: t.surface, border: `1px solid ${t.border}` }}
      >
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <svg
              className="animate-spin w-6 h-6"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke={t.border}
                strokeWidth="3"
              />
              <path
                d="M12 2a10 10 0 0 1 10 10"
                stroke={t.primary}
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
          </div>
        ) : keys.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Key className="w-10 h-10" style={{ color: t.border }} />
            <p
              className="text-[14px] font-medium"
              style={{ color: t.text.muted }}
            >
              Aucune clé API
            </p>
            <p className="text-[12px]" style={{ color: t.text.muted }}>
              Créez une clé pour permettre l'envoi de logs depuis des services
              externes.
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: `1px solid ${t.border}` }}>
                {[
                  "Nom",
                  "Clé (masquée)",
                  "Statut",
                  "Créée le",
                  "Dernière utilisation",
                  "",
                ].map((h) => (
                  <th
                    key={h}
                    className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wide"
                    style={{ color: t.text.muted }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {keys.map((k, i) => (
                <tr
                  key={k.id}
                  style={{
                    borderBottom:
                      i < keys.length - 1 ? `1px solid ${t.border}` : "none",
                    opacity: k.active ? 1 : 0.5,
                  }}
                >
                  <td className="px-5 py-4">
                    <div
                      className="text-[13px] font-semibold"
                      style={{ color: t.text.primary }}
                    >
                      {k.name}
                    </div>
                    {k.description && (
                      <div
                        className="text-[11px] mt-0.5"
                        style={{ color: t.text.muted }}
                      >
                        {k.description}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      <code
                        className="text-[12px] font-mono"
                        style={{ color: t.text.secondary }}
                      >
                        {k.keyPreview}
                      </code>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium"
                      style={{
                        backgroundColor: k.active ? "#dcfce7" : t.dangerBg,
                        color: k.active ? "#15803d" : t.danger,
                      }}
                    >
                      {k.active ? "Active" : "Révoquée"}
                    </span>
                  </td>
                  <td
                    className="px-5 py-4 text-[12px]"
                    style={{ color: t.text.secondary }}
                  >
                    {formatDate(k.createdAt)}
                    <div
                      className="text-[11px]"
                      style={{ color: t.text.muted }}
                    >
                      par {k.createdBy}
                    </div>
                  </td>
                  <td
                    className="px-5 py-4 text-[12px]"
                    style={{ color: t.text.secondary }}
                  >
                    {timeAgo(k.lastUsedAt)}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1.5 justify-end">
                      {k.active && (
                        <div className="relative group/revoke">
                          <button
                            onClick={() =>
                              setConfirmAction({
                                message: `Révoquer la clé "${k.name}" ? Elle ne pourra plus être utilisée.`,
                                action: () => revokeKey(k.id),
                              })
                            }
                            className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors"
                            style={{
                              border: `1px solid ${t.border}`,
                              background: "transparent",
                              color: "#f97316",
                              cursor: "pointer",
                            }}
                          >
                            <AlertTriangle className="w-3.5 h-3.5" />
                          </button>
                          <span
                            className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 whitespace-nowrap rounded-lg px-2.5 py-1 text-[11px] font-medium opacity-0 group-hover/revoke:opacity-100 transition-opacity z-50"
                            style={{
                              backgroundColor: "#1e1b4b",
                              color: "#fff",
                            }}
                          >
                            Révoquer
                          </span>
                        </div>
                      )}
                      <div className="relative group/delete">
                        <button
                          onClick={() =>
                            setConfirmAction({
                              message: `Supprimer définitivement la clé "${k.name}" ?`,
                              action: () => deleteKey(k.id),
                            })
                          }
                          className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors"
                          style={{
                            border: `1px solid ${t.border}`,
                            background: "transparent",
                            color: t.danger,
                            cursor: "pointer",
                          }}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                        <span
                          className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 whitespace-nowrap rounded-lg px-2.5 py-1 text-[11px] font-medium opacity-0 group-hover/delete:opacity-100 transition-opacity z-50"
                          style={{ backgroundColor: "#1e1b4b", color: "#fff" }}
                        >
                          Supprimer
                        </span>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {newKeyResult && (
        <NewKeyModal
          apiKey={newKeyResult}
          onClose={() => setNewKeyResult(null)}
        />
      )}
      {confirmAction && (
        <ConfirmModal
          message={confirmAction.message}
          onConfirm={() => {
            confirmAction.action();
            setConfirmAction(null);
          }}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </div>
  );
}
